import { formatYearMonthDate } from '@/utilities/dateHelpers';
import { IQualificationModule, ResultStatus, PreviousStudyLevel, IQualification, GradeSystem, IStudy } from 'types';

// Label mapping for PreviousStudyLevel enum
const previousStudyLevelLabels: Record<PreviousStudyLevel, string> = {
  [PreviousStudyLevel.HighSchool]: 'School / High School / College',
  [PreviousStudyLevel.Undergraduate]: 'Undergraduate',
  [PreviousStudyLevel.Postgraduate]: 'Postgraduate',
};

export function getFormattedQualificationModuleString(module: IQualificationModule | undefined): string {
  const subjectString = module?.subject || 'Not specified';
  const gradeString = module?.grade || 'N/A';

  return `${subjectString}: ${gradeString}`;
}

// Helper to get short results type label for display
export function getResultsTypeLabel(resultStatus: ResultStatus): string {
  switch (resultStatus) {
    case ResultStatus.FinalResults:
      return 'Final';
    case ResultStatus.InterimResults:
      return 'Predicted';
    case ResultStatus.NoResults:
      return 'No results yet';
    default:
      return 'Unknown';
  }
}

export function getStudyResultStatus(study?: IStudy): ResultStatus | undefined {
  if (!study) {
    return undefined;
  }

  // Check qualificationTypeSelections first (new structure)
  if (
    study?.qualificationTypeSelections &&
    Array.isArray(study.qualificationTypeSelections) &&
    study.qualificationTypeSelections.length > 0
  ) {
    // Find the first selection that has a resultStatus
    const selectionWithStatus = study.qualificationTypeSelections.find(sel => sel?.resultStatus !== undefined);
    if (selectionWithStatus?.resultStatus) {
      return selectionWithStatus.resultStatus;
    }
  }
  // Fall back to study-level resultStatus for old data
  return study?.resultStatus;
}

export function getStudyLabel(study?: IStudy, initialStudy?: IStudy): string {
  if (
    study &&
    study.qualificationTypeSelections &&
    Array.isArray(study.qualificationTypeSelections) &&
    study.qualificationTypeSelections.length > 0
  ) {
    // Join all qualification type labels with " & "
    return study.qualificationTypeSelections
      .map(sel => sel?.qualificationTypeLabel || sel?.customQualificationTypeName || sel?.qualificationType)
      .filter(Boolean)
      .join(' & ');
  }

  if (
    initialStudy &&
    initialStudy.qualificationTypeSelections &&
    Array.isArray(initialStudy.qualificationTypeSelections) &&
    initialStudy.qualificationTypeSelections.length > 0
  ) {
    // Join all qualification type labels with " & "
    return initialStudy.qualificationTypeSelections
      .map(sel => sel?.qualificationTypeLabel || sel?.customQualificationTypeName || sel?.qualificationType)
      .filter(Boolean)
      .join(' & ');
  }

  // Fallback to study level for old data
  return (
    previousStudyLevelLabels[study?.level as PreviousStudyLevel] ||
    study?.level ||
    previousStudyLevelLabels[initialStudy?.level as PreviousStudyLevel] ||
    initialStudy?.level ||
    'Unknown level'
  );
}

export function getGradeDisplay(
  isPending: boolean,
  studyResultStatus: ResultStatus | null,
  q: IQualification | undefined,
): string {
  if (!studyResultStatus) return `${q?.grade || 'N/A'}`;

  if (isPending) return 'Pending / Pending';

  if (studyResultStatus === ResultStatus.InterimResults) return `${q?.grade || 'N/A'} (predicted)`;

  if (studyResultStatus === ResultStatus.FinalResults) {
    return `${q?.grade || 'N/A'} / ${
      q?.gradeSystemLabel ||
      (q?.gradeSystem && GradeSystem[q.gradeSystem as keyof typeof GradeSystem]) ||
      q?.gradeSystem ||
      'N/A'
    }`;
  }

  if (studyResultStatus === ResultStatus.NoResults) return `${q?.grade || 'N/A'}`;

  console.warn('Unknown study result status:', studyResultStatus);

  return 'N/A';
}

export function getDateDisplay(isPending: boolean, q: IQualification | undefined): string {
  if (isPending) return 'Pending';

  if (q?.graduationDate) return formatYearMonthDate(q.graduationDate);

  return 'Not specified';
}
